import {
  TEXTS,
  INTEREST,
  DEFAULT_PROFILE,
  loadApplicantJobTitles,
  toEditableRow,
  newEditableRow,
  setRowInterest,
  setRowStarted,
  isRowDirty,
  isRowValid,
  buildRowRequests,
  markRowSaved,
  initialRanks,
  orderRows,
  moveUp,
  moveDown,
  keepPrimary,
  ranksChanged,
  buildRanksRequest,
  interpretRanksResponse,
  ranksErrorMessage,
  ownerText,
  editorTag
} from '@/custom/classes/applicant_job_titles.js'

// State + behaviour of the applicant job-title editor
// (entities/applicant/JobTitles.vue). Expects an
// `applicant` prop with `resource_id`.
export default {
  data () {
    return {
      selectedJobTitles: [],
      searchJobTitle: '',
      isSubmitted: false,
      dummy: 0,
      newServer: false,
      profile: Object.assign({}, DEFAULT_PROFILE),
      rankedIds: [],
      serverRankedIds: [],
      primaryId: null,
      serverPrimaryId: null,
      rankMessage: '',
      ranksSaving: false,
      rowsSaving: false,
      jobTitleTexts: TEXTS
    }
  },
  computed: {
    dropdownJobTitles () {
      const selectedIds = this.selectedJobTitles.map(title => title.id)
      return this.allJobTitles.filter(job => {
        return !selectedIds.includes(job.id) && job.name.includes(this.searchJobTitle)
      })
    },
    orderedJobTitles () {
      return orderRows(this.selectedJobTitles, this.rankedIds)
    },
    valideJobTitles () {
      const _ = this.dummy // eslint-disable-line
      const now = this.jobTitlesNow()
      return (
        this.selectedJobTitles.length > 0 &&
        this.selectedJobTitles.every(row => isRowValid(row, now)) &&
        !this.isSubmitted
      )
    },
    // older back-end cannot store "unknown": two states, as before
    interestStates () {
      if (this.newServer) return [INTEREST.INTERESTED, INTEREST.NOT_INTERESTED, INTEREST.UNKNOWN]
      return [INTEREST.INTERESTED, INTEREST.NOT_INTERESTED]
    },
    showRanks () {
      return this.newServer
    },
    ranksDirty () {
      return ranksChanged(this.rankedIds, this.primaryId, this.serverRankedIds, this.serverPrimaryId)
    },
    ranksOwnerText () {
      return ownerText(this.profile.ranks_owner)
    }
  },
  methods: {
    jobTitlesNow () {
      return new Date()
    },
    toast (text, type) {
      this.$utils.emit('show_toast', { text, type })
    },
    async loadJobTitles () {
      let loaded
      try {
        loaded = await loadApplicantJobTitles(this.$network_utils, this.applicant.resource_id)
      } catch (e) {
        this.toast(TEXTS.loadFailed, 'is-danger')
        return
      }
      if (this._isDestroyed) return
      const now = this.jobTitlesNow()
      this.newServer = loaded.newServer
      this.profile = loaded.profile
      this.isSubmitted = loaded.rows.length > 0
      this.selectedJobTitles = loaded.rows.map(row => toEditableRow(row, loaded.newServer, now))
      const { ranked, primary } = initialRanks(this.selectedJobTitles, loaded.profile)
      this.rankedIds = ranked
      this.serverRankedIds = ranked.slice()
      this.primaryId = primary
      this.serverPrimaryId = primary
      this.rankMessage = ''
      this.notifyParentElement()
    },
    notifyParentElement () {
      this.$emit('updateJobTitles', this.selectedJobTitles)
    },
    updateJobTitles () {
      this.isSubmitted = false
    },
    chooseJobTitle ({ target: { value: id } }) {
      const jobTitle = this.allJobTitles.find(j => j.id == id) // eslint-disable-line eqeqeq
      if (jobTitle) {
        this.selectedJobTitles.push(newEditableRow(jobTitle, this.newServer))
      }
    },
    async removeJotTitle (jobTitle) {
      if (jobTitle.server) {
        try {
          await this.$network_utils.deleteApplicantJobTitle({ applicant_id: this.applicant.resource_id, job_title_id: jobTitle.id })
        } catch (e) {
          this.toast(TEXTS.deleteFailed, 'is-danger')
          return
        }
      }
      this.selectedJobTitles = this.selectedJobTitles.filter(el => el.id !== jobTitle.id)
      this.rankedIds = this.rankedIds.filter(id => id !== jobTitle.id)
      this.serverRankedIds = this.serverRankedIds.filter(id => id !== jobTitle.id)
      this.primaryId = keepPrimary(this.rankedIds, this.primaryId)
      this.serverPrimaryId = keepPrimary(this.serverRankedIds, this.serverPrimaryId)
    },
    onStartedInput (jobTitle, value) {
      setRowStarted(jobTitle, value)
      this.dummy++
    },
    setInterest (jobTitle, state) {
      setRowInterest(jobTitle, state)
      this.dummy++
    },
    setStillActive (jobTitle, value) {
      jobTitle.still_active = !!value
      this.dummy++
    },
    isJobTitleDirty (jobTitle) {
      const _ = this.dummy // eslint-disable-line
      return isRowDirty(jobTitle, this.newServer, this.jobTitlesNow())
    },
    async submitJobTitles () {
      if (this.rowsSaving) return
      const requests = buildRowRequests(this.selectedJobTitles, this.applicant.resource_id, this.newServer, this.jobTitlesNow())
      if (requests.length === 0) {
        this.isSubmitted = true
        this.notifyParentElement()
        return
      }
      this.rowsSaving = true
      const results = await Promise.all(requests.map(req => {
        const call = req.method === 'put'
          ? this.$network_utils.updateApplicantJobTitle(req.params)
          : this.$network_utils.postApplicantJobTitle(req.params)
        return Promise.resolve(call).then(() => true, () => false)
      }))
      this.rowsSaving = false
      results.forEach((ok, i) => {
        if (ok) markRowSaved(requests[i].row, this.newServer)
      })
      this.dummy++
      if (results.every(Boolean)) {
        this.isSubmitted = true
      } else {
        this.toast(TEXTS.rowsFailed, 'is-danger')
      }
      this.notifyParentElement()
    },
    toggleExpireinceSelector ({ target: { value: exp } }, closeModal) {
      const [ jobId, selector ] = exp.split('|')
      const jobTitle = this.selectedJobTitles.find(j => j.id == jobId) // eslint-disable-line eqeqeq
      jobTitle.experienceSelector = selector
      jobTitle.started_at = null
      if (selector === 'no-exp') {
        jobTitle.has_experience = false
        jobTitle.started_edited = false
      } else {
        // the years/dates control was touched: a value is required and sent
        jobTitle.has_experience = true
        jobTitle.started_edited = true
      }
      this.dummy++
      closeModal()
    },
    // ---- ranks ----
    rankOf (jobTitle) {
      const i = this.rankedIds.indexOf(jobTitle.id)
      return i === -1 ? null : i + 1
    },
    canRank (jobTitle) {
      return !!jobTitle.server
    },
    applyRankMove (result) {
      if (result.error === 'cap') {
        this.rankMessage = TEXTS.rankCap
        return
      }
      this.rankMessage = ''
      this.rankedIds = result.ranked
      this.primaryId = keepPrimary(this.rankedIds, this.primaryId)
    },
    rankUp (jobTitle) {
      if (!this.canRank(jobTitle)) return
      this.applyRankMove(moveUp(this.rankedIds, jobTitle.id))
    },
    rankDown (jobTitle) {
      this.applyRankMove(moveDown(this.rankedIds, jobTitle.id))
    },
    setPrimary (jobTitle) {
      if (this.rankedIds.indexOf(jobTitle.id) !== -1) this.primaryId = jobTitle.id
    },
    editorTagOf (jobTitle) {
      return editorTag(jobTitle.last_editor)
    },
    async saveRanks () {
      if (!this.newServer) return // older back-end: no ranks route
      if (this.ranksSaving || !this.ranksDirty) return
      const body = buildRanksRequest(this.applicant.resource_id, this.rankedIds, this.primaryId)
      this.ranksSaving = true
      try {
        const response = await this.$network_utils.updateApplicantJobTitleRanks(body)
        const applied = interpretRanksResponse(response, body)
        if (!applied) {
          this.toast(TEXTS.ranksFailed, 'is-danger')
          return
        }
        this.rankedIds = applied.ranked
        this.serverRankedIds = applied.ranked.slice()
        this.primaryId = keepPrimary(applied.ranked, applied.primary)
        this.serverPrimaryId = this.primaryId
        this.profile = Object.assign({}, this.profile, {
          ranks_owner: 'recruiter',
          ranks_version: applied.ranks_version === null ? this.profile.ranks_version : applied.ranks_version,
          primary_job_title_id: this.primaryId
        })
        this.selectedJobTitles.forEach(row => {
          const i = applied.ranked.indexOf(row.id)
          row.title_rank = i === -1 ? null : i + 1
        })
        this.rankMessage = ''
        this.toast(TEXTS.ranksSaved, 'is-success')
      } catch (error) {
        this.toast(ranksErrorMessage(error), 'is-danger')
      } finally {
        this.ranksSaving = false
      }
    },
    getYearText (number) {
      if (!number) {
        return 'אין ניסיון'
      } else if (number === 1) {
        return 'שנה'
      } else if (number === 2) {
        return 'שנתיים'
      } else {
        return `${number} שנים`
      }
    }
  }
}
